import { useState } from "react";
import { Modal, Form, FormCheck, FormGroup, FormLabel } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { useText } from "../../context/TextContext";
import { AdvancedButton } from "../AdvancedButton";
import DealPicker from "../DealPicker";
import { formatDate } from "../../utils/date";
import { useCreateInvoiceFromTimeReports } from "../../hooks/useCreateInvoiceFromTimeReports";
import { toast } from "react-toastify";
import { PATHS } from "../../utils/shared/constants";

interface Props {
    onClose: () => void;
    initialStartDate: Date;
    initialEndDate: Date;
}

const parseLocalDate = (value: string, endOfDay: boolean): Date => {
    const [year, month, day] = value.split('-').map(Number);
    return endOfDay
        ? new Date(year, month - 1, day, 23, 59, 59, 999)
        : new Date(year, month - 1, day, 0, 0, 0, 0);
};

export default function CreateInvoiceModal({ onClose, initialStartDate, initialEndDate }: Props) {
    const text = useText();
    const navigate = useNavigate();
    const createInvoice = useCreateInvoiceFromTimeReports();

    const [dealId, setDealId] = useState('');
    const [dealError, setDealError] = useState(false);
    const [isPartial, setIsPartial] = useState(true);
    const [startDateString, setStartDateString] = useState(formatDate(initialStartDate, { isoDate: true, noTime: true }));
    const [endDateString, setEndDateString] = useState(formatDate(initialEndDate, { isoDate: true, noTime: true }));
    const [processing, setProcessing] = useState(false);

    const isDateRangeValid = !isPartial || (startDateString && endDateString && parseLocalDate(endDateString, true) >= parseLocalDate(startDateString, false));

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!dealId) {
            setDealError(true);
            return;
        }
        if (!isDateRangeValid) {
            return;
        }

        setProcessing(true);
        try {
            const invoice = await createInvoice({
                dealId,
                period: isPartial ? {
                    startDate: parseLocalDate(startDateString, false),
                    endDate: parseLocalDate(endDateString, true),
                } : undefined,
            });
            onClose();
            navigate(`${PATHS.DEALS}/${dealId}${PATHS.INVOICES}/${invoice.id}`);
        } catch (error) {
            console.error(error);
            toast.error(text.defaultError);
        }
        setProcessing(false);
    };

    return (
        <Modal show onHide={onClose} centered>
            <Modal.Header closeButton>
                <Modal.Title>{text.createInvoice}</Modal.Title>
            </Modal.Header>
            <Form onSubmit={handleSubmit}>
                <Modal.Body className="d-flex flex-column gap-3">
                    <FormGroup>
                        <FormLabel className="fw-semibold">{text.deal}</FormLabel>
                        <DealPicker
                            value={dealId}
                            onChange={(id) => { setDealId(id); setDealError(false); }}
                            error={dealError}
                        />
                    </FormGroup>

                    <FormCheck
                        type="switch"
                        id="partial-invoice-switch"
                        label={text.partialInvoice}
                        checked={isPartial}
                        onChange={(e) => setIsPartial(e.target.checked)}
                    />

                    {isPartial && (
                        <div className="d-flex align-items-center gap-2">
                            <div className="flex-grow-1">
                                <FormLabel className="fw-semibold small mb-1">{text.start}</FormLabel>
                                <Form.Control
                                    type="date"
                                    size="sm"
                                    value={startDateString}
                                    onChange={(e) => setStartDateString(e.target.value)}
                                    isInvalid={!isDateRangeValid}
                                />
                            </div>
                            <div className="flex-grow-1">
                                <FormLabel className="fw-semibold small mb-1">{text.end}</FormLabel>
                                <Form.Control
                                    type="date"
                                    size="sm"
                                    value={endDateString}
                                    onChange={(e) => setEndDateString(e.target.value)}
                                    isInvalid={!isDateRangeValid}
                                />
                            </div>
                        </div>
                    )}
                    {!isDateRangeValid && (
                        <div className="text-danger small">{text.endDateAfterStartDate}</div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <AdvancedButton variant="secondary" icon="bi-x" onClick={onClose}>{text.cancel}</AdvancedButton>
                    <AdvancedButton type="submit" icon="bi-floppy" processing={processing}>{text.create}</AdvancedButton>
                </Modal.Footer>
            </Form>
        </Modal>
    );
}
